import React, { useContext, useEffect, useState } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import axios from "axios";
import MainNavbar from "./MainNavbar";
import Footer from "./Footer";
import OpportunityCompanion from "./home/OpportunityCompanion";
import DisclaimerDialog from "../../DisclaimerDialog";
import { AuthContext } from "../../../context/AuthContext";

const DIVINE_INVITE_SESSION_KEY = "ucDivineRitualsInviteSeenV1";
const COMPANION_INVITE_SESSION_KEY = "ucCompanionInviteSeenV1";
const PROMOTION_LOCK_STORAGE_KEY = "ucActivePromotionV1";
const DIVINE_INVITE_DELAY_MS = 60000;
const DIVINE_INVITE_DURATION_MS = 8000;
const PROMOTION_RETRY_DELAY_MS = 5000;

const AppLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [providerDialogOpen, setProviderDialogOpen] = useState(false);
  const [showDivineInvite, setShowDivineInvite] = useState(false);
  const {
    isAuthenticated,
    profileUpdated,
    disclaimerAccepted,
    loading,
    setDisclaimerAccepted,
    logout,
  } = useContext(AuthContext);

  const isEnterprise = localStorage.getItem("isEnterprise") === "yes";
  const isProvider = localStorage.getItem("isprovider") === "yes";
  const isLoginPage = location.pathname === "/2.0/login";
  const isProfilePage = location.pathname === "/2.0/profile-update";
  const isClaudePracticePage = location.pathname === "/2.0/claude-practice-test";

  useEffect(() => {
    const promptSeen = sessionStorage.getItem("udyog2ProviderPromptSeen") === "yes";

    if (
      isAuthenticated &&
      !loading &&
      !isEnterprise &&
      isProvider &&
      disclaimerAccepted &&
      profileUpdated &&
      !promptSeen
    ) {
      setProviderDialogOpen(true);
      sessionStorage.setItem("udyog2ProviderPromptSeen", "yes");
    }
  }, [isAuthenticated, loading, isEnterprise, isProvider, disclaimerAccepted, profileUpdated]);

  useEffect(() => {
    let hideTimer;
    let retryTimer;

    if (isClaudePracticePage) {
      setShowDivineInvite(false);
      return undefined;
    }

    if (sessionStorage.getItem(DIVINE_INVITE_SESSION_KEY) === "yes") {
      setShowDivineInvite(false);
      return undefined;
    }

    const releasePromotionLock = () => {
      if (sessionStorage.getItem(PROMOTION_LOCK_STORAGE_KEY) === "divine-rituals") {
        sessionStorage.removeItem(PROMOTION_LOCK_STORAGE_KEY);
      }
    };

    const showOnceThisSession = () => {
      if (
        sessionStorage.getItem(COMPANION_INVITE_SESSION_KEY) !== "yes" ||
        sessionStorage.getItem(PROMOTION_LOCK_STORAGE_KEY)
      ) {
        retryTimer = window.setTimeout(showOnceThisSession, PROMOTION_RETRY_DELAY_MS);
        return;
      }

      sessionStorage.setItem(DIVINE_INVITE_SESSION_KEY, "yes");
      sessionStorage.setItem(PROMOTION_LOCK_STORAGE_KEY, "divine-rituals");
      setShowDivineInvite(true);
      hideTimer = window.setTimeout(() => {
        setShowDivineInvite(false);
        releasePromotionLock();
      }, DIVINE_INVITE_DURATION_MS);
    };

    setShowDivineInvite(false);
    const firstShowTimer = window.setTimeout(showOnceThisSession, DIVINE_INVITE_DELAY_MS);
    return () => {
      window.clearTimeout(firstShowTimer);
      window.clearTimeout(retryTimer);
      window.clearTimeout(hideTimer);
      releasePromotionLock();
    };
  }, [isClaudePracticePage]);

  const dismissDivineInvite = () => {
    setShowDivineInvite(false);
    if (sessionStorage.getItem(PROMOTION_LOCK_STORAGE_KEY) === "divine-rituals") {
      sessionStorage.removeItem(PROMOTION_LOCK_STORAGE_KEY);
    }
  };

  const handleAgree = async () => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/disclaimer/accept`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      setDisclaimerAccepted(true);
    } catch (error) {
      console.error("Error accepting disclaimer:", error);
    }
  };

  const handleLogout = () => {
    logout();
    window.location.href = "/2.0/login";
  };

  const openTutorialAssistant = () => {
    if (location.pathname === "/2.0" || location.pathname === "/2.0/") {
      window.dispatchEvent(new Event("udyog2-open-home-tour"));
      return;
    }

    navigate("/2.0", { state: { openTutorial: Date.now() } });
  };

  const openDivineRituals = () => {
    dismissDivineInvite();
    navigate("/uc/panditpurshothit");
  };

  if (isAuthenticated && loading) {
    return <div>Loading...</div>;
  }

  if (isAuthenticated && !isEnterprise && !isLoginPage && !disclaimerAccepted) {
    return <DisclaimerDialog open={true} onAgree={handleAgree} onLogout={handleLogout} />;
  }

  if (
    isAuthenticated &&
    !isEnterprise &&
    !isLoginPage &&
    !isProfilePage &&
    disclaimerAccepted &&
    !profileUpdated
  ) {
    return <Navigate to="/2.0/profile-update" replace state={{ from: location }} />;
  }

  return (
    <>
      <MainNavbar />
      <main style={{ padding: "30px" }}>
        <Outlet />
      </main>
      {!isClaudePracticePage && <div className="global-floating-rail" aria-label="Quick actions">
        <div className="divine-rituals-float-row">
          {showDivineInvite && (
            <div className="divine-rituals-invite" role="note">
              <button
                type="button"
                className="divine-rituals-invite-close"
                onClick={dismissDivineInvite}
                aria-label="Dismiss Divine Rituals invitation"
                title="Dismiss invitation"
              >
                ×
              </button>
              <strong>Having trouble finding a Pandit?</strong>
              <span>Book your Puja or ritual at the right muhurta for proper blessings.</span>
            </div>
          )}
          <button
            type="button"
            className="global-floating-action divine-rituals-launch-fab"
            data-tour="divine-rituals-fab"
            onClick={openDivineRituals}
            aria-label="Open Divine Rituals pooja booking"
            title="Book pooja with Divine Rituals"
          >
            <span className="divine-rituals-om-mark" aria-hidden="true">ॐ</span>
            <span className="launch-copy">
              <strong>Divine Rituals</strong>
              <small>New pooja booking</small>
            </span>
            <span className="launch-badge">New</span>
          </button>
        </div>
        <button
          type="button"
          className="global-floating-action tutorial-assistant-fab"
          onClick={openTutorialAssistant}
          aria-label="Show tutorial assistant"
          title="Show tutorial"
        >
          <HelpOutlineIcon fontSize="small" />
          <span className="tutorial-copy">Tutorial</span>
        </button>
      </div>}
      {!isClaudePracticePage && <OpportunityCompanion />}
      <Dialog
        open={providerDialogOpen}
        onClose={() => setProviderDialogOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 4,
            p: 1,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          Share Opportunities With the Community
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "grid", gap: 1 }}>
            <Typography variant="body1">
              We noticed you are registered as a provider.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              If you have job openings, mentoring support, business opportunities,
              or helpful leads, you can add them for Udyog Connect members to view.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setProviderDialogOpen(false)} variant="outlined">
            Later
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setProviderDialogOpen(false);
              navigate("/2.0/opportunities");
            }}
          >
            Add Opportunities
          </Button>
        </DialogActions>
      </Dialog>
      <Footer />
    </>
  );
};

export default AppLayout;
